import { AlertStatus, VigimareAlert } from '../../../models/vigimare-alert.model';
import {
  CyberAlertSeverity,
  CyberKillChain,
  CyberKillChainStage,
  CyberSite,
} from '../../../models/cyber-site.model';
import { CyberTopology, CyberTopologyNode } from '../../../models/cyber-topology.model';
import { CyberEvent } from './cyber-event.model';

/**
 * Pure derivation of the cyber page's visual state from VIGIMARE alerts.
 *
 * Nothing here is scripted against a use case. Which stage an alert belongs to comes from
 * the kill-chain config; which node it names comes from the topology the user drew; which
 * infrastructure it belongs to comes from the site configs. The reducer itself knows no
 * vocabulary of its own.
 *
 * No Angular, no timers, no I/O: `reduce` is a pure function and is unit tested as one.
 */

/** Bucket for alerts no stage matched. Visible on purpose, so the mapping can be tuned. */
export const UNCLASSIFIED_STAGE_ID = 'unclassified';

/** One alert's landing on a stage, in arrival order. */
export interface StageHit {
  alertId: number;
  alertUuid: string;
  at: string;
  /** Short line shown when the stage is expanded. */
  text: string;
}

export interface CyberState {
  /** stageId -> the alerts that landed there, oldest first. */
  stages: Readonly<Record<string, StageHit[]>>;
  /** Furthest stage reached. The story never rewinds. */
  currentStageId: string | null;
  narration: string | null;
  /** Nodes highlighted as compromised or implicated. */
  flaggedNodes: ReadonlySet<string>;
  /** Rolling window of metrics per node key, oldest first, when alerts quote numbers. */
  nodeTraffic: Readonly<Record<string, number[]>>;
  alarmCount: number;
  lastEventAt: string | null;
  /** Producer legal name -> ISO timestamp of its most recent message. */
  producersSeen: Readonly<Record<string, string>>;
}

const TRAFFIC_HISTORY = 60;
const STAGE_HIT_LIMIT = 100;

export function initialState(): CyberState {
  return {
    stages: {},
    currentStageId: null,
    narration: null,
    flaggedNodes: new Set<string>(),
    nodeTraffic: {},
    alarmCount: 0,
    lastEventAt: null,
    producersSeen: {},
  };
}

// ─── Stage helpers ───────────────────────────────────────────────────────────

function stageIndex(chain: CyberKillChain, stageId: string | null): number {
  if (stageId === null) return -1;
  return chain.stages.findIndex((s) => s.id === stageId);
}

export function currentStage(chain: CyberKillChain, state: CyberState): CyberKillChainStage | null {
  const index = stageIndex(chain, state.currentStageId);
  return index >= 0 ? chain.stages[index] : null;
}

/** True once an alert has landed on this stage or on any later one. */
export function hasReachedStage(
  chain: CyberKillChain,
  state: CyberState,
  stageId: string,
): boolean {
  const target = stageIndex(chain, stageId);
  return target >= 0 && stageIndex(chain, state.currentStageId) >= target;
}

/**
 * The last stage of the chain is the one that means real consequences, so reaching it — and
 * nothing short of it — puts the page into an alarm state.
 */
export function isAlarm(chain: CyberKillChain, state: CyberState): boolean {
  const last = chain.stages[chain.stages.length - 1];
  return last !== undefined && hasReachedStage(chain, state, last.id);
}

export function statusLabel(chain: CyberKillChain, state: CyberState): string {
  return currentStage(chain, state)?.label.toUpperCase() ?? 'NOMINAL';
}

function matchRegex(source: string | undefined, text: string): boolean {
  if (!source) return false;
  try {
    return new RegExp(source, 'i').test(text);
  } catch {
    // A malformed regex in the kill-chain config must not break the page.
    return false;
  }
}

/** Everything about an alert a stage regex is matched against. */
function alertText(alert: VigimareAlert): string {
  return [
    alert.anomaly?.anomalyType ?? '',
    alert.anomaly?.explanation ?? '',
    ...alert.intentions.map((i) => i.classification ?? ''),
    ...alert.intentions.map((i) => i.explanation ?? ''),
  ].join(' ');
}

/**
 * The furthest stage this alert matches, or null when none does.
 *
 * Furthest rather than first: an alert that mentions both the way in and the damage done is
 * reporting the damage. The same rule is asserted server-side in CyberKillChainServiceTest,
 * so the two stay in step.
 */
export function matchStage(
  chain: CyberKillChain,
  alert: VigimareAlert,
): CyberKillChainStage | null {
  const text = alertText(alert);
  let matched: CyberKillChainStage | null = null;
  for (const stage of chain.stages) {
    if (matchRegex(stage.match, text)) {
      matched = stage;
    }
  }
  return matched;
}

// ─── Node attribution ────────────────────────────────────────────────────────

/**
 * Which of the topology's nodes an alert names. Matches each node's key and aliases against
 * the alert's explanations; when nothing matches, every node is flagged so an unattributed
 * alert still shows on the diagram.
 *
 * Every node a user draws is watched, so every node is a candidate. Precision comes from the
 * keys and aliases: a node lights up because an alert named it, not because it exists.
 */
export function attributeNodes(topology: CyberTopology | null, alert: VigimareAlert): string[] {
  if (!topology) return [];

  const hits = namedNodes(topology, alert);
  return hits.length > 0 ? hits : topology.nodes.map((node) => node.nodeKey);
}

/**
 * The nodes an alert actually names — no fallback.
 *
 * What the diagram draws in red has to be earned. The page now also carries alerts from partners
 * whose prose is about indicators rather than devices (a MISP event's title names no PLC), and
 * under the fallback every one of those would light the whole schema and animate every link.
 * Silence is the honest answer: the alert is still listed, still on the story, just not attributed
 * to a device it never mentioned.
 */
export function namedNodes(
  topology: CyberTopology | null,
  alert: VigimareAlert,
  site: CyberSite | null = null,
): string[] {
  if (!topology) return [];

  const text = [
    alert.anomaly?.explanation ?? '',
    ...alert.intentions.map((i) => i.explanation ?? ''),
  ].join(' ');

  if (!text.trim()) return [];

  // A pattern that read nothing is wording this site does not describe — a detector update, a new
  // kind of finding — not an alert about nobody. Attributing nothing there would leave the diagram
  // silent during exactly the incident it exists for, so fall through and look the devices up
  // directly. Precise when the format is understood, best-effort when it is not, never blank.
  const subjects = alertSubjects(site, text);
  if (subjects !== null && subjects.length > 0) {
    // Captures that match no drawn node give nothing on purpose: the sentence *was* understood and
    // named a device this topology does not have, which is a gap in the drawing. Falling back here
    // would flag whatever else the boilerplate happens to mention.
    const byToken = tokenIndex(topology);
    const keys = new Set<string>();
    for (const subject of subjects) {
      const nodeKey = byToken.get(subject.toLowerCase());
      if (nodeKey !== undefined) keys.add(nodeKey);
    }
    return topology.nodes.map((n) => n.nodeKey).filter((key) => keys.has(key));
  }

  return topology.nodes
    .filter((node) => tokensOf(node).some((token) => mentions(text, token)))
    .map((node) => node.nodeKey);
}

/**
 * The devices this alert's sentences accuse, or null when the site does not say how to tell.
 *
 * Null rather than an empty list, because the two mean different things: "this site does not
 * describe its detectors' wording, fall back to reading the whole text" is not "this alert accuses
 * nobody". A pattern that compiles but matches nothing gives an empty list — an alert in a format
 * the site describes, which genuinely names no device.
 */
function alertSubjects(site: CyberSite | null, text: string): string[] | null {
  const source = site?.alertSubject?.pattern;
  if (!source) return null;

  let regex: RegExp;
  try {
    regex = new RegExp(source, 'gi');
  } catch {
    // A malformed pattern in the config must not blank the diagram.
    return null;
  }

  const subjects: string[] = [];
  for (const match of text.matchAll(regex)) {
    const node = match.groups?.['node'];
    if (node) subjects.push(node);
  }
  return subjects;
}

/** Every key and alias in the topology, lower-cased, pointing back at its node. */
function tokenIndex(topology: CyberTopology): Map<string, string> {
  const byToken = new Map<string, string>();
  for (const node of topology.nodes) {
    for (const token of tokensOf(node)) {
      if (token) byToken.set(token.toLowerCase(), node.nodeKey);
    }
  }
  return byToken;
}

/**
 * Every string that identifies this node inside an alert: its key, the name it is drawn under, and
 * any alias the author added.
 *
 * The label counts because a detector is as likely to write what the operator calls the device as
 * what the diagram keys it on. The cost is that a generic label matches generic prose — a node
 * named `Gateway` will answer to "IT-OT gateway vendor hit by ransomware" — so specific labels are
 * worth the trouble.
 */
function tokensOf(node: CyberTopologyNode): string[] {
  const tokens = new Set<string>([node.nodeKey, node.label, ...(node.aliases ?? [])]);
  return [...tokens].filter((token) => token != null && token.trim() !== '');
}

/**
 * Whether the prose names this device, case-insensitively and as a whole token — so `PLC1` does
 * not match inside `PLC10`.
 *
 * A number quoted for a device is a reading, not an accusation. Detectors append their window's
 * packet counts, `PLC1=342 PLC2=1067 …`, listing every device they measured whatever they found;
 * counting those as mentions implicates the whole plant on every alert. So a token immediately
 * followed by an assignment and a number does not count — but only at that spot, so a device named
 * in the sentence *and* quoted in the tail is still attributed.
 *
 * The same line is drawn server-side, where `CyberAlertDeduplicator` masks quoted values before
 * fingerprinting, and by `readAlertMetrics`, which reads exactly the pairs this ignores.
 */
function mentions(text: string, token: string): boolean {
  if (!token) return false;
  const escaped = token.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  return new RegExp(
    `(?<![A-Za-z0-9])${escaped}(?![A-Za-z0-9])(?!\\s*[=:]\\s*\\d)`,
    'i',
  ).test(text);
}

// ─── Traffic parsing ─────────────────────────────────────────────────────────

/**
 * Per-node numbers quoted inside an alert's prose, keyed by node key.
 *
 * Alerts carry explanations, not metrics, so this is opportunistic by design: a detector
 * that writes "PLC1=1204" gets a sparkline, one that does not gets none. Values are only
 * kept for keys the topology actually knows, so a stray "score=0.87" cannot invent a node.
 */
export function readAlertMetrics(
  site: CyberSite | null,
  topology: CyberTopology | null,
  alert: VigimareAlert,
): Record<string, number> {
  const pattern = site?.alertMetrics?.pattern;
  if (!pattern || !topology) return {};

  let regex: RegExp;
  try {
    regex = new RegExp(pattern, 'gi');
  } catch {
    return {};
  }

  const byToken = new Map<string, string>();
  for (const node of topology.nodes) {
    for (const token of tokensOf(node)) {
      if (token) byToken.set(token.toLowerCase(), node.nodeKey);
    }
  }

  const metrics: Record<string, number> = {};
  const text = [
    alert.anomaly?.explanation ?? '',
    ...alert.intentions.map((i) => i.explanation ?? ''),
  ].join(' ');

  for (const match of text.matchAll(regex)) {
    const node = match.groups?.['node'];
    const raw = match.groups?.['value'];
    if (!node || raw === undefined) continue;

    const nodeKey = byToken.get(node.toLowerCase());
    const value = Number(raw);
    if (nodeKey !== undefined && Number.isFinite(value)) {
      metrics[nodeKey] = value;
    }
  }
  return metrics;
}

// ─── Site scoping ────────────────────────────────────────────────────────────

function inBbox(site: CyberSite, lat: number | null, lon: number | null): boolean {
  const bbox = site.bbox;
  if (!bbox || lat === null || lon === null) return false;
  return lat >= bbox.minLat && lat <= bbox.maxLat && lon >= bbox.minLon && lon <= bbox.maxLon;
}

function belongsToSite(site: CyberSite, alert: VigimareAlert): boolean {
  if ((site.expectedProducers ?? []).includes(alert.legalName)) return true;
  return inBbox(site, alert.resolvedLatitude ?? alert.latitude, alert.resolvedLongitude ?? alert.longitude);
}

/**
 * Whether an alert concerns any monitored infrastructure. The page shows every configured
 * site at once, so scoping is a union — alerts from elsewhere still populate the map and the
 * alert list, they just must not drive the story or the diagram.
 */
export function belongsToAnySite(sites: readonly CyberSite[], alert: VigimareAlert): boolean {
  return sites.some((site) => belongsToSite(site, alert));
}

/**
 * The infrastructure this alert concerns, or null. Its config is what says how to read the
 * detector's wording, so anything attributing an alert to devices needs it first.
 */
export function siteOf(sites: readonly CyberSite[], alert: VigimareAlert): CyberSite | null {
  return sites.find((site) => belongsToSite(site, alert)) ?? null;
}

// ─── Assignment ──────────────────────────────────────────────────────────────

/**
 * Whether this alert is this user's to work on.
 *
 * Two sources because they answer at different moments. `assignedAlertIds` comes from the server,
 * which is the only party that knows the user's organizations — but it is a snapshot. The alert
 * itself carries its direct assignments, so one made seconds ago takes effect as soon as the
 * updated alert arrives, without waiting for the snapshot to be taken again.
 *
 * A null set means it has not been fetched yet, not that nothing is assigned; a caller using this
 * to hide alerts should treat that as "not yet known" rather than as "no".
 */
export function isAssignedTo(
  alert: VigimareAlert,
  userId: string,
  assignedAlertIds: ReadonlySet<number> | null,
): boolean {
  if (assignedAlertIds?.has(alert.id)) return true;
  return (alert.assignedUsers ?? []).some((assignment) => assignment.assigneeId === userId);
}

// ─── Settled alerts ──────────────────────────────────────────────────────────

/**
 * Statuses that mean the alert is no longer, by itself, a reason to show a live condition.
 *
 * An alert someone is working — acknowledged, in review, escalated — is still describing one, and
 * the diagram should go on saying so. Resolved, closed and false positive are not, and anything
 * still pulsing on their account is claiming something untrue.
 *
 * A duplicate is the same, for a different reason: the incident is already represented by the alert
 * it duplicates, and that one keeps its nodes flagged while it stays live. A second flag for the
 * same thing adds nothing, and would leave the diagram alight after the original was settled.
 *
 * Typed against `AlertStatus` so a status added later is a compile error here rather than a case
 * that silently falls through as unsettled.
 */
const SETTLED_STATUSES: ReadonlySet<AlertStatus> = new Set<AlertStatus>([
  'RESOLVED',
  'CLOSED',
  'FALSE_POSITIVE',
  'DUPLICATE',
]);

export function isSettled(alert: VigimareAlert): boolean {
  return SETTLED_STATUSES.has(alert.status);
}

// ─── Severity ────────────────────────────────────────────────────────────────

/** Score at or above which an ungraded detector's finding reads as critical. */
const CRITICAL_SCORE = 0.8;
/** …and as a warning. */
const WARNING_SCORE = 0.5;

/**
 * How seriously to take an alert.
 *
 * A detector's score is its confidence, not its severity: "80% sure a supplier was breached" is
 * not the claim "this is critical". So each site declares what a finding from each of its
 * producers means, and that is what grades the alert — whatever number it happens to carry.
 *
 * Two things still outrank the config. An operator who has taken or finished with an alert has
 * decided about it, and that decision is about the alert rather than about the detector. And a
 * producer no site grades falls back to reading the score, which is the best available guess for
 * a detector nobody has characterised yet.
 *
 * Note the asymmetry with the diagram: acknowledging is enough to calm the map, because the alert
 * has stopped clamouring for someone to look at it, while only settling calms the diagram, because
 * the condition itself is over.
 */
export function alertSeverity(
  sites: readonly CyberSite[],
  alert: VigimareAlert,
): CyberAlertSeverity {
  if (alert.status === 'ACKNOWLEDGED' || isSettled(alert)) {
    return 'nominal';
  }

  const declared = gradeOf(sites, alert.legalName);
  if (declared) return declared;

  const score =
    alert.anomaly?.anomalyScore ??
    (alert.intentions?.length > 0
      ? Math.max(...alert.intentions.map((intention) => intention.probability))
      : null);

  if (score === null) return 'unknown';
  if (score >= CRITICAL_SCORE) return 'critical';
  if (score >= WARNING_SCORE) return 'warning';
  return 'nominal';
}

/** The severity any site declares for this producer. */
function gradeOf(sites: readonly CyberSite[], legalName: string): CyberAlertSeverity | null {
  for (const site of sites) {
    const declared = site.producerSeverity?.[legalName];
    if (declared) return declared;
  }
  return null;
}

// ─── Reducer ─────────────────────────────────────────────────────────────────

export interface ReduceContext {
  sites: readonly CyberSite[];
  killChain: CyberKillChain;
  topology: CyberTopology | null;
}

/**
 * Folds one alert into the state. Alerts that concern no configured infrastructure are
 * recorded as "producer seen" only, so the services view stays honest without polluting the
 * story with someone else's incident.
 */
export function reduce(state: CyberState, event: CyberEvent, context: ReduceContext): CyberState {
  const { alert } = event;

  const producersSeen = { ...state.producersSeen };
  if (alert.legalName) {
    producersSeen[alert.legalName] = event.at;
  }

  if (!belongsToAnySite(context.sites, alert)) {
    return { ...state, producersSeen, lastEventAt: event.at };
  }

  const matched = matchStage(context.killChain, alert);
  const stageId = matched?.id ?? UNCLASSIFIED_STAGE_ID;

  // The story only ever moves forward; a late low-stage alert must not rewind it. The
  // unclassified bucket is off the chain entirely, so it never moves the pointer at all.
  const advance =
    matched !== null &&
    stageIndex(context.killChain, matched.id) > stageIndex(context.killChain, state.currentStageId);

  const site = siteOf(context.sites, alert);

  const flaggedNodes = new Set(state.flaggedNodes);
  const attributed = attributeNodes(context.topology, alert);
  const isIntention = alert.intentions.length > 0;
  if (isIntention && !isSettled(alert)) {
    // Only the nodes the alert names: flagging is a claim about a device, and the fallback that
    // keeps an unattributed alert visible on the diagram would make that claim about all of them.
    //
    // And only while the alert is still live. Nothing has to be unflagged when it settles: this is
    // a fold over the alerts currently listed, so the node simply stops being added, and the link
    // between two nodes stops reading as compromised as soon as either end does.
    namedNodes(context.topology, alert, site).forEach((nodeKey) => flaggedNodes.add(nodeKey));
  }

  const nodeTraffic = { ...state.nodeTraffic };
  for (const [nodeKey, value] of Object.entries(
    readAlertMetrics(site, context.topology, alert),
  )) {
    nodeTraffic[nodeKey] = [...(nodeTraffic[nodeKey] ?? []), value].slice(-TRAFFIC_HISTORY);
  }

  const explanation = alert.anomaly?.explanation ?? alert.intentions[0]?.explanation ?? null;

  return {
    ...state,
    producersSeen,
    lastEventAt: event.at,
    currentStageId: advance ? matched.id : state.currentStageId,
    stages: appendHit(state.stages, stageId, buildHit(alert, event.at, attributed)),
    flaggedNodes,
    nodeTraffic,
    narration: explanation ?? state.narration,
    alarmCount: state.alarmCount + (isIntention ? 1 : 0),
  };
}

function buildHit(alert: VigimareAlert, at: string, attributed: string[]): StageHit {
  const base = { alertId: alert.id, alertUuid: alert.uuid, at };

  if (alert.anomaly) {
    const score = alert.anomaly.anomalyScore?.toFixed(2) ?? '—';
    return { ...base, text: `${alert.anomaly.anomalyType} · score=${score}` };
  }

  const top = [...alert.intentions].sort((a, b) => b.probability - a.probability)[0];
  const nodes = attributed.length > 0 ? ` @ ${attributed.join(',')}` : '';
  return {
    ...base,
    text: top
      ? `${top.classification}${nodes} · p=${top.probability.toFixed(2)}`
      : `unclassified${nodes}`,
  };
}

/**
 * Upserts by alert uuid, because SSE delivers an `updated` event for an alert already on a
 * stage — a status change must not make the same alert count twice.
 */
function appendHit(
  stages: Readonly<Record<string, StageHit[]>>,
  stageId: string,
  hit: StageHit,
): Record<string, StageHit[]> {
  const next: Record<string, StageHit[]> = {};
  for (const [id, hits] of Object.entries(stages)) {
    next[id] = hits.filter((existing) => existing.alertUuid !== hit.alertUuid);
  }
  next[stageId] = [...(next[stageId] ?? []), hit].slice(-STAGE_HIT_LIMIT);
  return next;
}

// ─── Derived views used by the page ──────────────────────────────────────────

export function edgeKey(a: string, b: string): string {
  return `${a}→${b}`;
}

/**
 * How strongly a link is implicated.
 *
 * `compromised` is the detector's claim — the control path it says is being intercepted.
 * `exposed` is ours — a link that reaches a device under an open finding.
 */
export type CyberEdgeImpact = 'compromised' | 'exposed';

/**
 * How much a link is implicated by the alerts currently open.
 *
 * Edges carry no scripted phase to turn red on, so this is inferred from attribution — and the
 * inference is weaker than it looks, which is why there are two answers rather than one.
 *
 * `compromised` is what the detector claims: the control path. A finding on a controller is a
 * claim about the traffic between it and the gateway that polls it, so that link carries the
 * interception. Two accused devices either side of a link say the same thing more directly.
 *
 * `exposed` is our inference, not the detector's: a link that merely *reaches* an accused device.
 * An engineering workstation talking to a compromised controller is genuinely at risk, but no
 * alert said so, and drawing it with the same urgency would put words in the detector's mouth.
 *
 * Which end is a gateway comes from the topology's own node kinds, so nothing here knows what a
 * PLC or an HMI is.
 */
export function edgeImpacts(
  topology: CyberTopology | null,
  state: CyberState,
): Map<string, CyberEdgeImpact> {
  const impacts = new Map<string, CyberEdgeImpact>();
  if (!topology) return impacts;

  const kinds = new Map(topology.nodes.map((node) => [node.nodeKey, node.kind]));

  for (const edge of topology.edges) {
    const sourceAccused = state.flaggedNodes.has(edge.sourceKey);
    const targetAccused = state.flaggedNodes.has(edge.targetKey);
    if (!sourceAccused && !targetAccused) continue;

    const carriesControl =
      (sourceAccused && targetAccused) ||
      (sourceAccused && kinds.get(edge.targetKey) === 'gateway') ||
      (targetAccused && kinds.get(edge.sourceKey) === 'gateway');

    impacts.set(
      edgeKey(edge.sourceKey, edge.targetKey),
      carriesControl ? 'compromised' : 'exposed',
    );
  }
  return impacts;
}

// ─── Producer liveness ───────────────────────────────────────────────────────

export type ProducerHealth = 'active' | 'degraded' | 'offline';

export interface ProducerStatus {
  name: string;
  lastSeen: string | null;
  expected: boolean;
  health: ProducerHealth;
}

const ACTIVE_WINDOW_MS = 5 * 60 * 1000;
const DEGRADED_WINDOW_MS = 30 * 60 * 1000;

/**
 * Producer liveness across every configured infrastructure. Expected producers that have
 * never been seen are reported `offline` rather than omitted, so a silent detector is
 * visible as such.
 */
export function producerStatuses(
  sites: readonly CyberSite[],
  state: CyberState,
  now: number = Date.now(),
): ProducerStatus[] {
  const expected = new Set(sites.flatMap((site) => site.expectedProducers ?? []));
  const names = new Set<string>([...expected, ...Object.keys(state.producersSeen)]);

  return [...names]
    .map((name) => {
      const lastSeen = state.producersSeen[name] ?? null;
      const age = lastSeen ? now - new Date(lastSeen).getTime() : Number.POSITIVE_INFINITY;
      const health: ProducerHealth =
        age <= ACTIVE_WINDOW_MS ? 'active' : age <= DEGRADED_WINDOW_MS ? 'degraded' : 'offline';
      return { name, lastSeen, expected: expected.has(name), health };
    })
    .sort((a, b) => Number(b.expected) - Number(a.expected) || a.name.localeCompare(b.name));
}
